// Candidate export — turn candidate records into an Excel-ready grid.
//
// The recruiter/CEO wants the whole candidate list (or the current filtered
// view) as one spreadsheet — names, contact details, pipeline stage, recruiter,
// location — not clicked through 50 rows at a time. The Candidates page fetches
// the matching records (paging past the server's per-request cap) and hands the
// array here to build the sheet; the page then writes it with SheetJS, exactly
// like the Bulk Lookup export.
//
// Kept pure and separate so the column layout is unit-tested and never drifts
// from what the UI ships.

import type { Candidate } from './api';

/** The spreadsheet columns, in order. Header row of the export. */
export const CANDIDATE_EXPORT_HEADERS = [
  'First Name', 'Last Name', 'Email', 'Phone', 'Role', 'Specialties',
  'Stage', 'Status', 'Recruiter', 'City', 'State', 'ZIP',
  'Years Experience', 'Availability', 'Desired Pay', 'Source', 'LinkedIn',
] as const;

/** Column widths (characters) matched to CANDIDATE_EXPORT_HEADERS. */
export const CANDIDATE_EXPORT_COLW: { wch: number }[] = [
  { wch: 16 }, { wch: 16 }, { wch: 28 }, { wch: 16 }, { wch: 8 }, { wch: 26 },
  { wch: 14 }, { wch: 10 }, { wch: 20 }, { wch: 16 }, { wch: 8 }, { wch: 10 },
  { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 30 },
];

// Human labels for the availability enum; falls back to the raw value.
const AVAILABILITY_LABELS: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  per_diem: 'Per diem',
  contract: 'Contract',
};

function titleCase(s: string): string {
  return s.replace(/(^|[\s_-])([a-z])/g, (_, sep, ch) => (sep === '_' || sep === '-' ? ' ' : sep) + ch.toUpperCase());
}

/** One candidate → one spreadsheet row (a cell array aligned to the headers). */
export function candidateToExportRow(c: Candidate): (string | number)[] {
  const specialties = Array.isArray(c.specialties) ? c.specialties.filter(Boolean).join('; ') : '';
  const availability = c.availability_type
    ? (AVAILABILITY_LABELS[c.availability_type] ?? titleCase(c.availability_type))
    : '';
  // Pay is stored as a number (hourly/annual per tenant convention) — export the
  // raw number so the recipient can sort/sum in Excel; blank when unset.
  const desiredPay = typeof c.desired_pay_rate === 'number' && Number.isFinite(c.desired_pay_rate)
    ? c.desired_pay_rate
    : '';
  return [
    c.first_name ?? '',
    c.last_name ?? '',
    c.email ?? '',
    c.phone ?? '',
    c.role ?? '',
    specialties,
    c.stage ? titleCase(c.stage) : '',
    c.status ? titleCase(c.status) : '',
    c.recruiter_name ?? '',
    c.city ?? '',
    c.state ?? '',
    c.zip ?? '',
    typeof c.years_experience === 'number' && Number.isFinite(c.years_experience) ? c.years_experience : '',
    availability,
    desiredPay,
    c.source ?? '',
    c.linkedin_url ?? '',
  ];
}

/** Header row + one row per candidate — ready for XLSX.utils.aoa_to_sheet. */
export function buildCandidateSheetAoA(candidates: Candidate[]): (string | number)[][] {
  return [
    [...CANDIDATE_EXPORT_HEADERS],
    ...candidates.map(candidateToExportRow),
  ];
}
